import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { postCreateMission } from '../../../../queries/getVehicles.ts';
import { NodeType } from '../../../../schema/Node.ts';
import { CreateMission } from '../../../../schema/Vehicle.ts';
import { LoadingButton } from '../../../ui/Button.tsx';
import { SearchableSelect } from '../../../ui/Select.tsx';

interface Props {
  show: boolean;
  onHide: () => void;
  vehicleId: number;
  vehicleDisplayIdentifier: string;
  nodes: NodeType[] | undefined;
}

export function CreateMissionModal({
  show,
  onHide,
  vehicleId,
  vehicleDisplayIdentifier,
  nodes,
}: Readonly<Props>) {
  const { t } = useTranslation();
  const params = useParams() as { territorySlug: string };
  const [selectedNode, setSelectedNode] = useState<string>('');

  const createMissionMutation = useMutation({
    mutationFn: (payload: CreateMission) => postCreateMission(params.territorySlug, vehicleId, payload),
    onSuccess: () => {
      toast.success(`${t('vehicle.create-mission-toaster-success')} ${vehicleDisplayIdentifier}`);
      onHide();
    },
    onError: () => {
      toast.error(`${t('vehicle.create-mission-toaster-error')} ${vehicleDisplayIdentifier}`);
    },
  });

  const handleSubmit = () => {
    createMissionMutation.mutate({
      activity: 3,
      dispatch_destination_node_id: Number(selectedNode),
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton className={'border-0'}>
        <Modal.Title>
          <p className="card-subtitle">{vehicleDisplayIdentifier}</p>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="justify-content-between">
        {nodes && (
          <SearchableSelect
            placeholder={t('vehicle.create-mission-destination')}
            options={
              nodes?.map((node: NodeType) => ({
                value: String(node.id),
                label: node.name,
              })) ?? []
            }
            onChange={setSelectedNode}
          />
        )}
      </Modal.Body>
      <Modal.Footer className="border-0">
        <LoadingButton
          type="submit-button"
          text={t('vehicle.create-mission-submit')}
          imagePath="/theme/media/plus.svg"
          loading={createMissionMutation.isPending}
          onClick={handleSubmit}
          disabled={!selectedNode}
        />
      </Modal.Footer>
    </Modal>
  );
}
